import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserCircleGear, Warning, ShieldWarning, CheckCircle } from "@phosphor-icons/react";

export function RedFlagList({ flags, testid = "red-flags" }) {
  if (!flags?.length) {
    return (
      <div className="flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800" data-testid={`${testid}-clear`}>
        <CheckCircle size={14} weight="fill" /> No red flags on record
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid={testid}>
      {flags.map((f) => (
        <div key={f.code}
          className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium ${
            f.severity === "critical" ? "border-red-300 bg-red-50 text-red-800"
            : f.severity === "warning" ? "border-amber-300 bg-amber-50 text-amber-800"
            : "border-slate-200 bg-slate-50 text-slate-600"}`}
          data-testid={`${testid}-${f.code}`}>
          <ShieldWarning size={13} weight="fill" /> {f.label}
        </div>
      ))}
    </div>
  );
}

const ISSUE_TYPES = [
  ["dispute", "Dispute"], ["claim", "Claim"], ["warning", "Warning"], ["suspension", "Suspension"],
];

function IssuesRegister({ vendorId, issues, canEdit, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "dispute", title: "", description: "", value: 0 });

  const add = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    try {
      await api.post(`/vendors/${vendorId}/issues`, form);
      toast.success(`${form.type} recorded`);
      setShowForm(false); setForm({ type: "dispute", title: "", description: "", value: 0 });
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resolve = async (id) => {
    const resolution = window.prompt("Resolution note:");
    if (resolution == null) return;
    try {
      await api.patch(`/vendors/issues/${id}`, { status: "resolved", resolution });
      toast.success("Issue resolved");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="mt-4" data-testid="v360-issues">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Disputes · Claims · Warnings · Suspensions</h4>
        {canEdit && (
          <Button size="sm" variant="outline" className="h-7" onClick={() => setShowForm(!showForm)} data-testid="v360-issue-add-btn">Record issue</Button>
        )}
      </div>
      {showForm && (
        <div className="mt-2 grid grid-cols-12 gap-2 rounded-md border border-amber-200 bg-amber-50/40 p-3">
          <div className="col-span-3">
            <Label className="text-[10px]">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="h-8" data-testid="v360-issue-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ISSUE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-5">
            <Label className="text-[10px]">Title *</Label>
            <Input className="h-8" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="v360-issue-title" />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px]">Value (USD)</Label>
            <Input className="h-8" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value || 0) })} data-testid="v360-issue-value" />
          </div>
          <div className="col-span-2 flex items-end">
            <Button size="sm" className="h-8 w-full btn-primary-blue" onClick={add} data-testid="v360-issue-submit">Save</Button>
          </div>
          <div className="col-span-12">
            <Textarea rows={2} placeholder="Description…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
      )}
      {issues.length === 0 ? (
        <div className="mt-2 rounded-md border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">No disputes, claims, warnings or suspensions on record.</div>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {issues.map((i) => (
            <li key={i.id} className="flex items-start gap-2 rounded-md border border-slate-200 p-2.5 text-xs" data-testid={`v360-issue-${i.id}`}>
              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                i.type === "dispute" || i.type === "suspension" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{i.type}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800">{i.title} {i.value > 0 && <span className="font-mono-alt text-slate-500">· {formatCurrency(i.value)}</span>}</div>
                {i.description && <div className="text-slate-500">{i.description}</div>}
                <div className="text-[10px] text-slate-400">{formatDate(i.date)} · raised by {(i.raised_by || "").split("@")[0]}
                  {i.status === "resolved" && <> · resolved by {(i.resolved_by || "").split("@")[0]}{i.resolution ? ` — ${i.resolution}` : ""}</>}
                </div>
              </div>
              {i.status === "open" ? (
                canEdit && <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => resolve(i.id)} data-testid={`v360-issue-resolve-${i.id}`}>Resolve</Button>
              ) : (
                <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-700">resolved</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Stat({ label, value, tone = "slate" }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2.5">
      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-0.5 font-display text-sm font-bold tabular-nums ${tone === "red" ? "text-red-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function EngagementTable({ title, rows, testid }) {
  return (
    <div className="rounded-md border border-slate-200" data-testid={testid}>
      <div className="border-b border-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">{title} ({rows.length})</div>
      {rows.length === 0 ? <div className="p-3 text-center text-xs text-slate-400">None</div> : (
        <table className="w-full text-xs">
          <tbody>
            {rows.map((e) => (
              <tr key={e.procurement_id} className="border-t border-slate-50">
                <td className="px-3 py-1.5 font-mono-alt text-[10px]">{e.ref}</td>
                <td className="px-2 py-1.5"><span className="max-w-[200px] truncate inline-block" title={e.title}>{e.title}</span>
                  {e.project_name && <div className="text-[10px] text-slate-400">{e.project_name}{e.project_location ? ` · 📍 ${e.project_location}` : ""}</div>}
                </td>
                <td className="px-2 py-1.5 text-[10px] uppercase text-slate-500">{e.stream} · {e.phase} · {e.status}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-mono-alt">{formatCurrency(e.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Vendor360Drawer({ vendorId, vendorName, canEdit }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data: d } = await api.get(`/vendors/${vendorId}/profile360`);
      setData(d); setErr("");
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { if (open) { setData(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const f = data?.financial;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`v360-open-${vendorId}`}>
        <UserCircleGear size={13} className="mr-1" /> 360° Profile
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="v360-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <UserCircleGear size={18} /> Subcontractor 360° — {vendorName}
            </DialogTitle>
            <DialogDescription>Consolidated history, performance, compliance and financial position across all modules.</DialogDescription>
          </DialogHeader>
          {err && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{err}</div>}
          {!data && !err && <div className="p-6 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-400">Loading…</div>}
          {data && (
            <div>
              <RedFlagList flags={data.red_flags} testid="v360-flags" />

              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Total contracted" value={formatCurrency(f.total_contracted)} />
                <Stat label="Total billed" value={formatCurrency(f.total_billed)} />
                <Stat label="Paid to date" value={formatCurrency(f.total_paid)} />
                <Stat label="Outstanding balance" value={formatCurrency(f.outstanding_balance)} tone={f.outstanding_balance > 0 ? "red" : "slate"} />
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <EngagementTable title="Current engagements" rows={data.engagements.current} testid="v360-current" />
                <EngagementTable title="Previous engagements" rows={data.engagements.previous} testid="v360-previous" />
              </div>

              {/* Performance */}
              <section className="mt-4" data-testid="v360-performance">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Performance</h4>
                <div className="mt-1.5 flex flex-wrap gap-2 text-xs">
                  {data.performance.avl ? (
                    <span className={`rounded-full px-2 py-1 font-semibold ${
                      data.performance.avl.avl_status === "AVL" ? "bg-emerald-50 text-emerald-700"
                      : data.performance.avl.avl_status === "WATCH" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
                      AVL: {data.performance.avl.avl_status} · score {data.performance.avl.score}
                    </span>
                  ) : <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-500">Not yet evaluated (no F-PROC-14)</span>}
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{data.performance.ncrs.length} NCR(s)</span>
                  <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{data.performance.reviews.length} review(s)</span>
                </div>
                {data.performance.reviews.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {data.performance.reviews.map((r, i) => (
                      <li key={i} className="rounded-md border border-slate-200 p-2">
                        <span className="font-mono-alt text-[10px] text-slate-400 mr-1">{r.form} · {r.ref}</span>
                        {r.outcome && <>Outcome: <b>{r.outcome}</b> · overall {r.overall_score}/5 · engage again: {r.would_engage_again}</>}
                        {r.delivery_score != null && <>Delivery {r.delivery_score} · Quality {r.quality_score} · Documentation {r.documentation_score}</>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* Compliance */}
              <section className="mt-4" data-testid="v360-compliance">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Compliance &amp; documents</h4>
                <div className="mt-1.5 text-xs text-slate-600">
                  Licence: {data.compliance.license_no || "—"}{data.compliance.license_expiry ? ` · expires ${formatDate(data.compliance.license_expiry)}` : ""}
                  · {data.compliance.certificates.length} certificate(s)
                </div>
                {data.compliance.expired_documents.length > 0 && (
                  <div className="mt-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="v360-expired-docs">
                    <Warning size={12} className="inline mr-1" weight="fill" />
                    Expired: {data.compliance.expired_documents.map((d) => `${d.label} (${formatDate(d.expiry)})`).join(", ")}
                  </div>
                )}
              </section>

              <IssuesRegister vendorId={vendorId} issues={data.issues} canEdit={canEdit} onChanged={load} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
